import { useState } from 'react';
import { Form, Row, Col, DatePicker, Input, Button, message } from 'antd';
import regex from '../utils/helper';
import moment from 'moment';
import { ModalComponent } from '@operations/shared';
import { useDispatch } from 'react-redux';
import { request } from '../../../shared/src/apis';
import { errorMessage } from '../utils/commonStringsConsts';
import { addUnidentifiedCollectionsApi } from '../utils/apisConsts';

const AddUnidentifiedCollection = () => {
    const validation: any = regex.regexValidation;
    const [form] = Form.useForm();
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [pendingValues, setPendingValues] = useState<any>(null);
    const [submitting, setSubmitting] = useState(false);
    const dispatch = useDispatch();

    const handleSubmit = async (values: any) => {
        setPendingValues(values);
        setConfirmModalVisible(true);
    };

    const handleConfirm = async () => {
        if (!pendingValues) return;
        
        setSubmitting(true);
        try {
            const payload = {
                amount: Number(pendingValues.amount),
                collectionDate: moment(pendingValues.collectionDate).format('YYYY-MM-DD HH:mm:ss'),
                ...(pendingValues.comment ? { comment: pendingValues.comment } : {})
            };
            const response: any = await dispatch(request({
                url: addUnidentifiedCollectionsApi,
                method: 'POST',
                data: payload,
            }));
            if (response?.data?.status === 200 || response?.data?.status === 201) {
                message.success(response?.data?.message || 'Collection added successfully', 4);
            } else {
                message.error(response?.data?.message || errorMessage, 4);
                return;
            }

            setConfirmModalVisible(false);
            form.resetFields();
            setPendingValues(null);
        } catch (error) {
            console.error('Error submitting collection:', error);
            message.error(errorMessage, 4);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        setConfirmModalVisible(false);
        setPendingValues(null);
    };

    return (
        <div style={{ marginTop: '1rem' }}>
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Row gutter={24}>
                    <Col span={12}>
                        <label>Collection Amount <span>*</span></label>
                        <Form.Item
                            name="amount"
                            rules={[
                                { required: true, message: 'Amount is required' },
                                {
                                    validator: (_, value) => {
                                        if (value === undefined || value === null || value === '') {
                                            return Promise.resolve();
                                        }
                                        if (!validation.positiveFloatNumbers.test(String(value))) {
                                            return Promise.reject('Amount must be a valid number');
                                        }
                                        const numericValue = Number(value);
                                        if (Number.isNaN(numericValue) || numericValue < 1) {
                                            return Promise.reject('Amount must be greater than 0');
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                            style={{ marginTop: 2 }}
                        >
                            <Input style={{ width: '100%' }} placeholder="Enter the collection amount" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <label>Collection Date <span>*</span></label>
                        <Form.Item
                            name="collectionDate"
                            rules={[
                                { required: true, message: 'Collection Date is required' },
                                {
                                    validator: (_, value) => {
                                        if (!value) {
                                            return Promise.resolve();
                                        }
                                        if (value && moment(value).isAfter(moment(), 'day')) {
                                            return Promise.reject('Collection Date cannot be a future date');
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                             style={{ marginTop: 2 }}
                        >
                            <DatePicker placeholder="Enter the collection date" format="YYYY/MM/DD" style={{ width: '100%' }} disabledDate={(current) => { return current && current > moment().endOf('day') }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24} style={{ marginBottom: '0.5rem', marginTop: '0.5rem' }}>
                    <Col span={24}>
                        <label>Comment</label>
                        <Form.Item name="comment" style={{ marginTop: 2 }}>
                            <Input.TextArea placeholder="Add your comment" autoSize={{ minRows: 2, maxRows: 4 }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row style={{ justifyContent: 'end' }}>
                    <Col span={4}>
                        <Form.Item>
                            <Button type="primary" shape="round" block htmlType="submit" style={{ marginTop: '0.5rem', paddingInline: '1.5rem' }}>
                                Submit
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <ModalComponent
                title="Confirm Submission"
                visibleModal={confirmModalVisible}
                handleAcceptModal={handleConfirm}
                handleCloseModal={handleCancel}
                okText="Confirm"
                cancelText="Cancel"
                loadingConfirm={submitting}
                content={(
                    <p style={{ textAlign: 'center' }}>
                        The collection of <strong>{pendingValues?.amount || 0} LE</strong> will be added as an unidentified collection,<br />would you like to proceed?
                    </p>
                )}
            />
        </div>
    );
};

export default AddUnidentifiedCollection;